import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {OpenInNew} from "@mui/icons-material";
import {
    DataGridColumnAlignments,
    DataGridColumnTypes,
    useDataGridSavedState,
} from "../../../../../../../../../packages/data-grid";
import {PurchaseHistoryListsReportDataGridCellKeys} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {ReactComponent as Passed} from "../../../../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../../../../assets/images/validations/failed.svg";

/**
 * Purchase history report table — same PartnerDataGrid pattern as invoices report.
 */
const PurchaseHistoryListReportSection = ({
    searchData,
    data,
    dataGridSaveConfig,
    navigateToHistory,
    defaultVendor,
    getExportInfo,
}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchases.historyList.reportColumns'});

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]} */
        [
            {
                name: PurchaseHistoryListsReportDataGridCellKeys.navigate,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: 0,
            },
            {
                title: t('historyNo'),
                name: PurchaseHistoryListsReportDataGridCellKeys.historyNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: true,
            },
            ...(!defaultVendor?.id ? [{
                title: t('vendor'),
                name: PurchaseHistoryListsReportDataGridCellKeys.vendor,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            }] : []),
            {
                title: t('date'),
                name: PurchaseHistoryListsReportDataGridCellKeys.date,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.date,
            },
            {
                title: t('vendorPO'),
                name: PurchaseHistoryListsReportDataGridCellKeys.vendorPO,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: t('avgCost'),
                name: PurchaseHistoryListsReportDataGridCellKeys.avgCost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
            },
            {
                title: t('discount'),
                name: PurchaseHistoryListsReportDataGridCellKeys.discountAmount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
            },
            {
                title: t('subtotal'),
                name: PurchaseHistoryListsReportDataGridCellKeys.subtotal,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 150,
            },
            {
                title: t('total'),
                name: PurchaseHistoryListsReportDataGridCellKeys.total,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 120,
            },
            {
                title: t('balance'),
                name: PurchaseHistoryListsReportDataGridCellKeys.balance,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 120,
            },
            {
                title: t('status'),
                name: PurchaseHistoryListsReportDataGridCellKeys.status,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
            },
        ], [defaultVendor?.id, t]);

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    const dataGridRows = useMemo(() => (data ?? []).map((row) => {
        const vendorId = defaultVendor?.id ?? row.vendor?.id ?? row.vendorId;
        return {
            key: row.id,
            data: row,
            cells: {
                [PurchaseHistoryListsReportDataGridCellKeys.navigate]: (
                    <IconButton
                        color={'secondary'}
                        onPressed={() => navigateToHistory(row, vendorId)}
                    >
                        <OpenInNew className={'icon normal'}/>
                    </IconButton>
                ),
                [PurchaseHistoryListsReportDataGridCellKeys.historyNo]: row.historyNo,
                [PurchaseHistoryListsReportDataGridCellKeys.vendor]:
                    row.vendor?.name ?? row.vendor?.title ?? '',
                [PurchaseHistoryListsReportDataGridCellKeys.date]: row.date,
                [PurchaseHistoryListsReportDataGridCellKeys.vendorPO]: row.vendorPO ?? '',
                [PurchaseHistoryListsReportDataGridCellKeys.avgCost]: row.avgCost,
                [PurchaseHistoryListsReportDataGridCellKeys.discountAmount]: row.discountAmount,
                [PurchaseHistoryListsReportDataGridCellKeys.subtotal]: row.subTotal,
                [PurchaseHistoryListsReportDataGridCellKeys.total]: row.total,
                [PurchaseHistoryListsReportDataGridCellKeys.balance]: row.balance,
                [PurchaseHistoryListsReportDataGridCellKeys.status]:
                    !row.isOutstanding
                        ? <Passed/>
                        : <Failed/>,
            },
        };
    }), [data, defaultVendor?.id, navigateToHistory]);

    return (
        <PartnerDataGrid
            {...dataGridSaveConfig}
            initialLayoutState={{columns}}
            columns={dataGridColumns}
            rows={dataGridRows}
            state={{
                pagination: searchData.paginationInfo,
                sortBy: searchData.orderBy,
                loading: {state: searchData.loading},
                density: dataGridDensity,
            }}
            hideFooterActions
            getExportInfo={getExportInfo}
            onSortByChanged={(_, current) => searchData.setOrderBy(current)}
            onCurrentPageChanged={(_, current) => searchData.setPaginationInfo((prev) => ({...prev, ...current}))}
            onPageSizeChanged={(_, current) => searchData.setPaginationInfo((prev) => ({...prev, ...current}))}
        />
    );
};

export default PurchaseHistoryListReportSection;
